import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { SelectItem } from 'primeng/api';
import { Plant, PlannerGroup, WorkCenter } from '../../../models/plant.model';
import { ContextService } from '../../../core/services/context.service';
import { selectPlants } from '../../../store/selectors/plant.selector';
import * as plantActions from '../../../store/actions/plant.actions';
import { FILTER_VALUE } from '../../constants/filters.constants';
import { DEFAULT_OPTION } from '../../constants/default-options.constans';

export interface PlantFilterSelectionChange {
  plant: string;
  plannerGroups: string[];
  workCenters: string[];
}

@Component({
  selector: 'app-plant-filters',
  templateUrl: './plant-filters.component.html',
  standalone: false,
})
export class PlantFiltersComponent implements OnInit, OnDestroy {
  @Output() filterChange = new EventEmitter<PlantFilterSelectionChange>();

  selectedPlant: string = FILTER_VALUE.ALL;
  selectedPlannerGroups: string[] = [];
  selectedWorkCenters: string[] = [];

  plantOptions: SelectItem[] = [];
  plannerGroupOptions: SelectItem[] = [];
  workCenterOptions: SelectItem[] = [];

  plants: Plant[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private contextService: ContextService,
  ) {}

  ngOnInit(): void {
    this.selectedPlant = this.contextService.getSelectedPlant();
    this.selectedPlannerGroups = this.contextService.getSelectedPlannerGroups();
    this.selectedWorkCenters = this.contextService.getSelectedWorkCenters();

    this.store.dispatch(plantActions.loadPlants());

    this.store
      .select(selectPlants)
      .pipe(takeUntil(this.destroy$))
      .subscribe((plants) => {
        if (!plants) return;
        this.plants = plants;
        this.plantOptions = [
          ...DEFAULT_OPTION,
          ...plants.map((p: Plant) => ({
            label: p.sapDescription || p.sapId,
            value: p.sapId,
          })),
        ];
        this.updatePlannerGroupOptions();
        this.updateWorkCenterOptions();
      });
  }

  onPlantChange(value: string): void {
    this.contextService.setSelectedPlant(value);
    this.selectedPlannerGroups = [];
    this.selectedWorkCenters = [];
    this.contextService.setSelectedPlannerGroups([]);
    this.contextService.setSelectedWorkCenters([]);
    this.updatePlannerGroupOptions();
    this.updateWorkCenterOptions();
    this.emitChange();
  }

  onPlannerGroupsChange(values: string[]): void {
    this.contextService.setSelectedPlannerGroups(values);
    this.selectedWorkCenters = [];
    this.contextService.setSelectedWorkCenters([]);
    this.updateWorkCenterOptions();
    this.emitChange();
  }

  onWorkCentersChange(values: string[]): void {
    this.contextService.setSelectedWorkCenters(values);
    this.emitChange();
  }

  private updatePlannerGroupOptions(): void {
    if (this.selectedPlant === FILTER_VALUE.ALL || !this.selectedPlant) {
      const allGroups = this.plants.flatMap((p) => p.plannerGroups ?? []);
      this.plannerGroupOptions = allGroups.map((pg: PlannerGroup) => ({
        label: pg.sapDescription || pg.sapId,
        value: pg.sapId,
      }));
    } else {
      const plant = this.plants.find((p) => p.sapId === this.selectedPlant);
      this.plannerGroupOptions = (plant?.plannerGroups ?? []).map(
        (pg: PlannerGroup) => ({
          label: pg.sapDescription || pg.sapId,
          value: pg.sapId,
        }),
      );
    }
  }

  private updateWorkCenterOptions(): void {
    let plannerGroups: PlannerGroup[] = [];

    if (this.selectedPlannerGroups.length > 0) {
      plannerGroups = this.plants
        .flatMap((p) => p.plannerGroups ?? [])
        .filter((pg) => this.selectedPlannerGroups.includes(pg.sapId));
    } else if (this.selectedPlant !== FILTER_VALUE.ALL && this.selectedPlant) {
      const plant = this.plants.find((p) => p.sapId === this.selectedPlant);
      plannerGroups = plant?.plannerGroups ?? [];
    } else {
      plannerGroups = this.plants.flatMap((p) => p.plannerGroups ?? []);
    }

    const allWorkCenters = plannerGroups.flatMap((pg) => pg.workCenters ?? []);
    this.workCenterOptions = allWorkCenters.map((wc: WorkCenter) => ({
      label: wc.sapDescription || wc.sapId,
      value: wc.sapId,
    }));
  }

  private emitChange(): void {
    this.filterChange.emit({
      plant: this.selectedPlant,
      plannerGroups: this.selectedPlannerGroups,
      workCenters: this.selectedWorkCenters,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
